import Phaser from 'phaser';

export class MobileControls {
  private scene: Phaser.Scene;
  private joystickBase: Phaser.GameObjects.Graphics;
  private joystickThumb: Phaser.GameObjects.Graphics;
  private boostButton: Phaser.GameObjects.Container;
  private boostButtonBg: Phaser.GameObjects.Graphics;
  private boostButtonIcon: Phaser.GameObjects.Text;

  private joystickBaseX: number = 0;
  private joystickBaseY: number = 0;
  private joystickRadius: number = 50;
  private thumbRadius: number = 22;

  // 이미지 위치 (터치 위치와 분리)
  private joystickImageX: number = 0;
  private joystickImageY: number = 0;

  private buttonX: number = 0;
  private buttonY: number = 0;
  private buttonSize: number = 65;

  // 이미지 위치 (터치 위치와 분리)
  private buttonImageX: number = 0;
  private buttonImageY: number = 0;

  private isJoystickActive: boolean = false;
  private isBoosting: boolean = false;
  private direction: number = 0;
  private joystickPointerId: number = -1;

  private onDirectionChange?: (direction: number) => void;
  private onBoostChange?: (isBoosting: boolean) => void;

  private resizeHandler: () => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // 조이스틱 베이스
    this.joystickBase = scene.add.graphics();
    this.joystickBase.setScrollFactor(0);
    this.joystickBase.setDepth(200);

    // 조이스틱 썸
    this.joystickThumb = scene.add.graphics();
    this.joystickThumb.setScrollFactor(0);
    this.joystickThumb.setDepth(201);

    // 부스트 버튼 배경
    this.boostButtonBg = scene.add.graphics();
    this.boostButtonBg.setScrollFactor(0);
    this.boostButtonBg.setDepth(200);

    // 불꽃 아이콘
    this.boostButtonIcon = scene.add.text(0, 0, '🔥', {
      fontSize: '32px',
    });
    this.boostButtonIcon.setOrigin(0.5);
    this.boostButtonIcon.setScrollFactor(0);
    this.boostButtonIcon.setDepth(201);

    // 버튼 컨테이너
    this.boostButton = scene.add.container(0, 0);
    this.boostButton.setDepth(200);

    // 초기 위치 계산 및 그리기
    this.updatePositions();

    // 터치 이벤트 설정
    this.setupTouchEvents();

    // 화면 크기 변경 이벤트 리스너 (방향 전환 대응)
    this.resizeHandler = () => {
      // 약간의 딜레이 후 위치 업데이트 (화면 크기 안정화 대기)
      setTimeout(() => this.updatePositions(), 100);
    };
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);
  }

  /**
   * 화면 좌표 → 카메라 뷰 좌표 변환.
   * 이 컨트롤은 줌이 적용된 GameScene 카메라에 그려지는데,
   * setScrollFactor(0)은 스크롤만 무시할 뿐 줌 스케일은 그대로 받는다.
   * 줌 Z에서 뷰 좌표 P는 화면상 center + (P - center) × Z 에 그려지므로,
   * 화면 위치 S에 보이게 하려면 P = center + (S - center) / Z 로 역변환해야 한다.
   * (과거에는 가로 모드 기준 고정 오프셋(±160, +100)으로 때워서
   *  세로 모드에서 터치 위치와 아이콘 위치가 어긋났다)
   */
  private toViewCoords(screenX: number, screenY: number): { x: number; y: number } {
    const cam = this.scene.cameras.main;
    const zoom = cam.zoom || 1;
    const cx = cam.width / 2;
    const cy = cam.height / 2;
    return {
      x: cx + (screenX - cx) / zoom,
      y: cy + (screenY - cy) / zoom,
    };
  }

  /** 화면 픽셀 크기를 뷰 좌표 크기로 변환 (줌 역보정) */
  private viewScale(): number {
    return 1 / (this.scene.cameras.main.zoom || 1);
  }

  /**
   * 화면 크기 기반으로 위치 업데이트
   * 터치 판정은 화면 좌표(joystickBaseX 등), 그리기는 뷰 좌표(joystickImageX 등)를 쓰며
   * 두 좌표는 toViewCoords로 정확히 일치한다 — 모든 화면 크기/방향에서 동일.
   */
  private updatePositions(): void {
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;

    // 조이스틱 터치 위치 (왼쪽 하단) - 화면 좌표
    const joystickPadding = 30;
    this.joystickBaseX = joystickPadding + this.joystickRadius + 40;
    this.joystickBaseY = height - joystickPadding - this.joystickRadius - 30;

    // 부스트 버튼 터치 위치 - 화면 좌표
    // 가로: 미니맵 왼쪽 / 세로: 미니맵이 숨겨지므로 오른쪽 가장자리로 보내
    // 조이스틱(왼쪽)과 좌우로 넓게 벌린다 (양손 파지 시 엄지 위치)
    const isPortrait = height > width;
    const buttonPadding = 30;
    const minimapOffset = isPortrait ? 0 : 120;
    this.buttonX = width - buttonPadding - this.buttonSize / 2 - minimapOffset;
    this.buttonY = height - buttonPadding - this.buttonSize / 2 - 30;

    // 그리기 위치 = 터치 위치의 뷰 좌표 변환 (정확히 같은 화면 지점)
    const joystickView = this.toViewCoords(this.joystickBaseX, this.joystickBaseY);
    this.joystickImageX = joystickView.x;
    this.joystickImageY = joystickView.y;

    const buttonView = this.toViewCoords(this.buttonX, this.buttonY);
    this.buttonImageX = buttonView.x;
    this.buttonImageY = buttonView.y;

    // 그래픽 다시 그리기
    this.drawJoystickBase();
    this.drawJoystickThumb(this.joystickImageX, this.joystickImageY);
    this.drawBoostButton(this.isBoosting);
    this.boostButtonIcon.setPosition(this.buttonImageX, this.buttonImageY);
    this.boostButtonIcon.setScale(this.viewScale());
  }

  private drawJoystickBase(): void {
    const s = this.viewScale();
    this.joystickBase.clear();
    this.joystickBase.fillStyle(0x000000, 0.3);
    this.joystickBase.fillCircle(this.joystickImageX, this.joystickImageY, this.joystickRadius * s);
    this.joystickBase.lineStyle(2 * s, 0xFFFFFF, 0.4);
    this.joystickBase.strokeCircle(this.joystickImageX, this.joystickImageY, this.joystickRadius * s);
  }

  private drawJoystickThumb(x: number, y: number): void {
    const s = this.viewScale();
    this.joystickThumb.clear();
    // 그림자
    this.joystickThumb.fillStyle(0x000000, 0.2);
    this.joystickThumb.fillCircle(x + 2 * s, y + 2 * s, this.thumbRadius * s);
    // 썸
    this.joystickThumb.fillStyle(0xFFFFFF, 0.8);
    this.joystickThumb.fillCircle(x, y, this.thumbRadius * s);
    this.joystickThumb.fillStyle(0x4D96FF, 0.7);
    this.joystickThumb.fillCircle(x, y, (this.thumbRadius - 4) * s);
  }

  private drawBoostButton(pressed: boolean): void {
    const s = this.viewScale();
    this.boostButtonBg.clear();

    if (pressed) {
      this.boostButtonBg.fillStyle(0xFF6600, 0.9);
      this.boostButtonBg.fillCircle(this.buttonImageX, this.buttonImageY, (this.buttonSize / 2 - 2) * s);
      this.boostButtonBg.lineStyle(3 * s, 0xFFFFFF, 1);
      this.boostButtonBg.strokeCircle(this.buttonImageX, this.buttonImageY, (this.buttonSize / 2 - 2) * s);
    } else {
      this.boostButtonBg.fillStyle(0xFF4444, 0.6);
      this.boostButtonBg.fillCircle(this.buttonImageX, this.buttonImageY, (this.buttonSize / 2) * s);
      this.boostButtonBg.lineStyle(2 * s, 0xFFFFFF, 0.6);
      this.boostButtonBg.strokeCircle(this.buttonImageX, this.buttonImageY, (this.buttonSize / 2) * s);
    }
  }

  private setupTouchEvents(): void {
    // 터치 시작
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // 화면 좌표 직접 사용 (setScrollFactor(0) 요소는 화면 좌표계 사용)
      const px = pointer.x;
      const py = pointer.y;

      // 부스트 버튼 체크
      const buttonRadius = this.buttonSize / 2 + 20;
      const distToBoost = Phaser.Math.Distance.Between(px, py, this.buttonX, this.buttonY);
      if (distToBoost <= buttonRadius) {
        this.isBoosting = true;
        this.drawBoostButton(true);
        this.onBoostChange?.(true);
        return;
      }

      // 조이스틱 영역 체크 (왼쪽 절반)
      const screenWidth = this.scene.scale.width;
      if (px < screenWidth / 2 && this.joystickPointerId === -1) {
        this.isJoystickActive = true;
        this.joystickPointerId = pointer.id;
        this.updateJoystick(px, py);
      }
    });

    // 터치 이동
    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isJoystickActive && pointer.id === this.joystickPointerId) {
        this.updateJoystick(pointer.x, pointer.y);
      }
    });

    // 터치 종료
    this.scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      // 조이스틱 리셋
      if (pointer.id === this.joystickPointerId) {
        this.isJoystickActive = false;
        this.joystickPointerId = -1;
        this.drawJoystickThumb(this.joystickImageX, this.joystickImageY);
      }

      // 부스트 해제 - 모든 포인터 체크
      const activePointers = this.scene.input.manager.pointers.filter(p => p.isDown);
      const buttonRadius = this.buttonSize / 2 + 20;
      const boostStillPressed = activePointers.some(p => {
        const dist = Phaser.Math.Distance.Between(p.x, p.y, this.buttonX, this.buttonY);
        return dist <= buttonRadius;
      });

      if (!boostStillPressed && this.isBoosting) {
        this.isBoosting = false;
        this.drawBoostButton(false);
        this.onBoostChange?.(false);
      }
    });
  }

  private updateJoystick(pointerX: number, pointerY: number): void {
    // 방향/클램프 계산은 전부 화면 좌표에서 수행
    const dx = pointerX - this.joystickBaseX;
    const dy = pointerY - this.joystickBaseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    let screenThumbX = pointerX;
    let screenThumbY = pointerY;

    if (distance > this.joystickRadius) {
      // 범위 밖이면 경계에 위치
      const angle = Math.atan2(dy, dx);
      screenThumbX = this.joystickBaseX + Math.cos(angle) * this.joystickRadius;
      screenThumbY = this.joystickBaseY + Math.sin(angle) * this.joystickRadius;
    }

    // 그리기 직전에만 뷰 좌표로 변환
    const thumbView = this.toViewCoords(screenThumbX, screenThumbY);
    this.drawJoystickThumb(thumbView.x, thumbView.y);

    // 방향 계산
    if (distance > 10) {
      this.direction = Math.atan2(dy, dx);
      this.onDirectionChange?.(this.direction);
    }
  }

  setOnDirectionChange(callback: (direction: number) => void): void {
    this.onDirectionChange = callback;
  }

  setOnBoostChange(callback: (isBoosting: boolean) => void): void {
    this.onBoostChange = callback;
  }

  getDirection(): number {
    return this.direction;
  }

  isBoostPressed(): boolean {
    return this.isBoosting;
  }

  isActive(): boolean {
    return this.isJoystickActive;
  }

  destroy(): void {
    // 이벤트 리스너 제거
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('orientationchange', this.resizeHandler);

    this.joystickBase.destroy();
    this.joystickThumb.destroy();
    this.boostButtonBg.destroy();
    this.boostButtonIcon.destroy();
    this.boostButton.destroy();
  }
}
